import { useCallback } from 'react'

import { MAX_TOTAL_TRADES } from 'constants/defaults'
import Connector from 'containers/Connector'
import {
	fetchCrossMarginAccountData,
	fetchCrossMarginMarketData,
	fetchCrossMarginPendingOrders,
	fetchCrossMarginPositions,
	fetchCrossMarginAccountTrades,
	fetchPerpsV3Account,
	fetchSnxV3GlobalTradesForMarket,
	fetchCrossMarginOrderHistory,
	fetchDashboardChartDataV3,
	fetchPerpsV3GlobalLiquidations,
	fetchCrossMarginAccountLiquidations,
} from 'state/futures/snxPerpsV3/actions'
import {
	selectCrossMarginAccount,
	selectProviderSupportsCrossMargin,
	selectV3SelectedMarketId,
} from 'state/futures/snxPerpsV3/selectors'
import { fetchOptimismMarkets } from 'state/home/actions'
import { useAppSelector, useFetchAction, usePollAction } from 'state/hooks'
import {
	fetchAllReferralData,
	fetchBoostNftForAccount,
	fetchBoostNftMinted,
} from 'state/referrals/action'
import { fetchStakeMigrateData } from 'state/staking/actions'
import {
	selectEpochPeriod,
	selectSelectedEpoch,
	selectStakingSupportedNetwork,
	selectTradingRewardsSupportedNetwork,
} from 'state/staking/selectors'
import {
	selectAbstractionAddress,
	selectIsWatcherMode,
	selectProviderNetwork,
	selectWallet,
} from 'state/wallet/selectors'

import {
	fetchAbstractionDelegateStatus,
	fetchAvgOneClickTxConst,
	fetchFuturesPositionHistory,
	fetchMarginTransfers,
} from './actions'
import { selectPerpsProvider } from './common/selectors'
import { selectMarkets } from './selectors'
import {
	fetchSmartMarginOrdersForAccount,
	fetchAllV2TradesForAccount,
	fetchDelegatesForSMAccount,
	fetchFuturesFees,
	fetchFuturesFeesForAccount,
	fetchSmartMarginAccount,
	fetchSmartMarginAccountData,
	fetchSmartMarginMarketData,
	fetchSmartMarginOpenOrders,
	fetchSubAccountsForSMAccount,
	fetchSnxV2GlobalTradesForMarket,
} from './snxPerpsV2/actions'
import {
	selectSmartMarginAccount,
	selectWalletChainSupportsSmartMargin,
	selectClientChainSupportsSmartMargin,
} from './snxPerpsV2/selectors'
import { PerpsProvider } from './types'

// TODO: Optimise polling and queries
export const usePollMarketFuturesData = () => {
	const networkId = useAppSelector(selectProviderNetwork)
	const markets = useAppSelector(selectMarkets)
	const wallet = useAppSelector(selectWallet)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const smartMarginAddress = useAppSelector(selectSmartMarginAccount)
	const crossMarginAccount = useAppSelector(selectCrossMarginAccount)
	const selectedPerpsProvider = useAppSelector(selectPerpsProvider)
	const clientSupportsSmartMargin = useAppSelector(selectClientChainSupportsSmartMargin)
	const networkSupportsCrossMargin = useAppSelector(selectProviderSupportsCrossMargin)
	const selectedMarket = useAppSelector(selectV3SelectedMarketId)
	const networkSupportTradingRewards = useAppSelector(selectTradingRewardsSupportedNetwork)

	useFetchAction(fetchBoostNftMinted, {
		dependencies: [wallet],
		disabled: isWatcherMode || !networkSupportTradingRewards,
	})

	useFetchAction(fetchBoostNftForAccount, {
		dependencies: [wallet],
		disabled: isWatcherMode || !networkSupportTradingRewards,
	})

	useFetchAction(fetchSmartMarginAccount, {
		dependencies: [networkId, wallet, selectedPerpsProvider],
		disabled:
			!wallet || !clientSupportsSmartMargin || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})

	useFetchAction(fetchPerpsV3Account, {
		dependencies: [networkId, wallet, selectedPerpsProvider],
		disabled:
			!wallet || !networkSupportsCrossMargin || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	useFetchAction(fetchDelegatesForSMAccount, {
		dependencies: [networkId, wallet, smartMarginAddress],
		disabled: !wallet || !smartMarginAddress || !clientSupportsSmartMargin,
	})

	useFetchAction(fetchSubAccountsForSMAccount, {
		dependencies: [networkId, wallet, smartMarginAddress],
		disabled: !wallet || !smartMarginAddress || !clientSupportsSmartMargin,
	})

	usePollAction('fetchCrossMarginGlobalTrades', fetchSnxV3GlobalTradesForMarket, {
		dependencies: [networkId, selectedPerpsProvider, selectedMarket],
		disabled: !networkSupportsCrossMargin || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchSnxV2GlobalTradesForMarket', fetchSnxV2GlobalTradesForMarket, {
		dependencies: [networkId, selectedPerpsProvider, selectedMarket],
		disabled: !clientSupportsSmartMargin || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})

	usePollAction('fetchPerpsV3GlobalLiquidations', fetchPerpsV3GlobalLiquidations, {
		dependencies: [networkId, selectedPerpsProvider, selectedMarket],
		disabled: !networkSupportsCrossMargin || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	useFetchAction(fetchMarginTransfers, {
		dependencies: [networkId, wallet, selectedPerpsProvider],
	})

	usePollAction('fetchSmartMarginMarketData', fetchSmartMarginMarketData, {
		dependencies: [networkId],
		intervalTime: 60000,
		disabled: !clientSupportsSmartMargin || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})

	usePollAction('fetchCrossMarginMarketData', fetchCrossMarginMarketData, {
		dependencies: [networkId],
		intervalTime: 60000,
		disabled: !networkSupportsCrossMargin || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchCrossMarginPositions', fetchCrossMarginPositions, {
		intervalTime: 30000,
		dependencies: [wallet, markets.length],
		disabled: !wallet || !markets.length || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})
	usePollAction('fetchSmartMarginAccountData', fetchSmartMarginAccountData, {
		intervalTime: 30000,
		dependencies: [markets.length, smartMarginAddress],
		disabled:
			!markets.length || !smartMarginAddress || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})
	usePollAction('fetchCrossMarginAccountData', fetchCrossMarginAccountData, {
		intervalTime: 30000,
		dependencies: [markets.length, crossMarginAccount],
		disabled:
			!markets.length || !crossMarginAccount || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})
	usePollAction('fetchFuturesPositionHistory', fetchFuturesPositionHistory, {
		intervalTime: 15000,
		dependencies: [wallet, smartMarginAddress, crossMarginAccount],
		disabled: !wallet,
	})
	usePollAction('fetchCrossMarginOpenOrders', fetchCrossMarginPendingOrders, {
		dependencies: [networkId, wallet, markets.length, selectedPerpsProvider],
		intervalTime: 10000,
		disabled: !wallet || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchSmartMarginOpenOrders', fetchSmartMarginOpenOrders, {
		dependencies: [networkId, wallet, markets.length, smartMarginAddress],
		intervalTime: 10000,
		disabled: !wallet || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})

	usePollAction('fetchAllV2TradesForAccount', fetchAllV2TradesForAccount, {
		dependencies: [networkId, wallet, smartMarginAddress, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !wallet,
	})

	usePollAction('fetchCrossMarginAccountTrades', fetchCrossMarginAccountTrades, {
		dependencies: [networkId, wallet, crossMarginAccount, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !crossMarginAccount || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchCrossMarginAccountLiquidations', fetchCrossMarginAccountLiquidations, {
		dependencies: [networkId, wallet, crossMarginAccount, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !crossMarginAccount || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchSmartMarginOrdersForAccount', fetchSmartMarginOrdersForAccount, {
		dependencies: [networkId, wallet, smartMarginAddress, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !wallet || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})

	usePollAction('fetchCrossMarginOrderHistory', fetchCrossMarginOrderHistory, {
		dependencies: [networkId, wallet, crossMarginAccount, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !wallet || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})
}

export const usePollDashboardFuturesData = () => {
	const networkId = useAppSelector(selectProviderNetwork)
	const markets = useAppSelector(selectMarkets)
	const wallet = useAppSelector(selectWallet)
	const smartMarginAccount = useAppSelector(selectSmartMarginAccount)
	const networkSupportsCrossMargin = useAppSelector(selectProviderSupportsCrossMargin)
	const signerSupportsSmartMargin = useAppSelector(selectWalletChainSupportsSmartMargin)
	const providerSupportsSmartMargin = useAppSelector(selectClientChainSupportsSmartMargin)
	const selectedPerpsProvider = useAppSelector(selectPerpsProvider)
	const crossMarginAccount = useAppSelector(selectCrossMarginAccount)

	useFetchAction(fetchPerpsV3Account, {
		dependencies: [networkId, wallet, selectedPerpsProvider],
		disabled:
			!wallet || !networkSupportsCrossMargin || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	useFetchAction(fetchMarginTransfers, {
		dependencies: [networkId, wallet, smartMarginAccount, crossMarginAccount],
		disabled: !wallet,
	})

	useFetchAction(fetchSmartMarginAccount, {
		dependencies: [networkId, wallet],
		disabled: !wallet || !signerSupportsSmartMargin,
	})

	usePollAction('fetchSmartMarginMarketData', fetchSmartMarginMarketData, {
		dependencies: [networkId],
		intervalTime: 60000,
		disabled: !providerSupportsSmartMargin || selectedPerpsProvider !== PerpsProvider.SNX_V2,
	})

	usePollAction('fetchCrossMarginMarketData', fetchCrossMarginMarketData, {
		dependencies: [networkId],
		intervalTime: 60000,
		disabled: !networkSupportsCrossMargin,
	})

	usePollAction('fetchFuturesPositionHistory', fetchFuturesPositionHistory, {
		intervalTime: 15000,
		dependencies: [wallet, smartMarginAccount, crossMarginAccount],
		disabled: !wallet,
	})

	usePollAction('fetchSmartMarginAccountData', fetchSmartMarginAccountData, {
		intervalTime: 30000,
		dependencies: [smartMarginAccount, markets.length, networkId],
		disabled: !markets.length || !wallet,
	})

	usePollAction('fetchCrossMarginAccountData', fetchCrossMarginAccountData, {
		intervalTime: 30000,
		dependencies: [markets.length, crossMarginAccount, selectedPerpsProvider],
		disabled:
			!markets.length || !crossMarginAccount || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction(
		'fetchAllV2TradesForAccount',
		() => fetchAllV2TradesForAccount({ pageSize: MAX_TOTAL_TRADES }),
		{
			dependencies: [networkId, wallet, selectedPerpsProvider, smartMarginAccount],
			intervalTime: 30000,
			disabled: !wallet,
		}
	)

	usePollAction('fetchCrossMarginAccountTrades', fetchCrossMarginAccountTrades, {
		dependencies: [networkId, wallet, crossMarginAccount, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !crossMarginAccount || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchCrossMarginOrderHistory', fetchCrossMarginOrderHistory, {
		dependencies: [networkId, wallet, crossMarginAccount, selectedPerpsProvider],
		intervalTime: 30000,
		disabled: !wallet || selectedPerpsProvider !== PerpsProvider.SNX_V3,
	})

	usePollAction('fetchDashboardChartDataV3', fetchDashboardChartDataV3, {
		dependencies: [networkId, wallet, crossMarginAccount],
		intervalTime: 30000,
		disabled: !crossMarginAccount,
	})
}

export const useFetchStakeMigrateData = () => {
	const networkId = useAppSelector(selectProviderNetwork)
	const wallet = useAppSelector(selectWallet)
	const { start, end } = useAppSelector(selectSelectedEpoch)
	const networkSupportsStaking = useAppSelector(selectStakingSupportedNetwork)

	useFetchAction(fetchStakeMigrateData, {
		dependencies: [networkId, wallet],
	})

	useFetchAction(fetchFuturesFees, {
		dependencies: [networkId, start, end],
		disabled: !networkSupportsStaking,
	})

	useFetchAction(fetchFuturesFeesForAccount, {
		dependencies: [networkId, wallet, start, end],
		disabled: !wallet || !networkSupportsStaking,
	})
}

export const useFetchReferralData = () => {
	const networkId = useAppSelector(selectProviderNetwork)
	const wallet = useAppSelector(selectWallet)
	const period = useAppSelector(selectEpochPeriod)
	const networkSupportTradingRewards = useAppSelector(selectTradingRewardsSupportedNetwork)

	useFetchAction(fetchAllReferralData, {
		dependencies: [networkId, wallet, period],
		disabled: !networkSupportTradingRewards || !wallet || !period,
	})
}

export const useFetchAbstractionData = () => {
	const smAddress = useAppSelector(selectSmartMarginAccount)
	const accountId = useAppSelector(selectCrossMarginAccount)
	const aaAddress = useAppSelector(selectAbstractionAddress)
	const networkId = useAppSelector(selectProviderNetwork)

	useFetchAction(fetchAbstractionDelegateStatus, {
		dependencies: [smAddress, accountId, aaAddress, networkId],
		disabled: !aaAddress,
	})

	usePollAction('futures/fetchAvgOneClickTxConst', fetchAvgOneClickTxConst, {
		dependencies: [smAddress, accountId, aaAddress, networkId],
		intervalTime: 45000,
		disabled: !aaAddress,
	})
}

export const useFetchLandingPageMarketData = () => {
	const { optimismClient } = Connector.useContainer()
	const networkId = useAppSelector(selectProviderNetwork)
	const providerSupportsSmartMargin = useAppSelector(selectClientChainSupportsSmartMargin)

	const fetchMarketsByL2Provider = useCallback(
		// @ts-ignore
		() => fetchOptimismMarkets(optimismClient),
		[optimismClient]
	)

	useFetchAction(fetchMarketsByL2Provider)
	useFetchAction(fetchSmartMarginMarketData, {
		dependencies: [networkId],
		disabled: !providerSupportsSmartMargin,
	})
}
